// Portfolio (screen 7, slice-2-onboarding.md §10; extends mockup A1). Loading/error/
// empty/populated states; "+ Add an app" is viewer-gated (§2.3) - greyed with a tooltip,
// never silently hidden.
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { isErr } from "@nightcrew/shared";
import type { AppView } from "@nightcrew/shared";
import { listApps } from "../api/apps.js";
import { isOwnerOrAdmin, useSession } from "../state/session.js";
import { useWizard } from "../state/wizard.js";
// Brand-supplied illustration (docs/design/mockups/assets/glim.svg, copied verbatim into
// this package - never generated, slice-2-onboarding.md:687-688). brand.css's own
// `.glim-badge img { width: 26px }` (docs/design/mockups/brand.css:196-197) sizes it; the
// explicit width/height attributes below just pin the intrinsic aspect ratio (250:190) so
// the badge never causes layout shift (responsive-design's image-dimension guideline).
import glimIllustration from "../assets/glim.svg";

type LoadState =
  | { readonly status: "loading" }
  | { readonly status: "error" }
  | { readonly status: "ready"; readonly apps: readonly AppView[] };

/** Maps the App record onto the extended status set (slice-2-onboarding.md §10.2). Slice
 * 2 has no fix/incident data yet, so every fully-onboarded app reads "Healthy" until a
 * later slice adds "Needs a look". */
const statusFor = (app: AppView): { readonly label: string; readonly tag: string } => {
  if (app.routeConfirmedAt === null) return { label: "Confirming route", tag: "info" };
  return { label: "Healthy", tag: "good" };
};

export const Portfolio = (): React.JSX.Element => {
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const { state: session } = useSession();
  const { reset: resetWizard } = useWizard();
  const navigate = useNavigate();
  const canAddApp = isOwnerOrAdmin(session);

  useEffect(() => {
    const cancelled = { current: false };
    void (async () => {
      const result = await listApps();
      if (cancelled.current) return;
      setState(isErr(result) ? { status: "error" } : { status: "ready", apps: result.value.apps });
    })();
    return () => {
      cancelled.current = true;
    };
  }, []);

  const handleAddApp = (): void => {
    resetWizard();
    void navigate("/apps/new");
  };

  if (state.status === "loading") {
    return (
      <div>
        <div className="kpi-row">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="card kpi-card">
              <div className="skeleton skeleton-line" />
            </div>
          ))}
        </div>
        <div className="stack gap12 mt24">
          {[0, 1, 2].map((i) => (
            <div key={i} className="card skeleton-card" />
          ))}
        </div>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="card state-card">
        <p>We could not load your apps. Try again.</p>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            setState({ status: "loading" });
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (state.apps.length === 0) {
    return (
      <div className="empty-state">
        <div className="glim-badge">
          <img src={glimIllustration} alt="Glim, the Nightcrew mascot" width={26} height={20} />
        </div>
        <h2 className="h2">No apps yet</h2>
        <p className="muted">Add your first one and the crew gets to work tonight.</p>
        {canAddApp ? (
          <button type="button" className="btn btn-primary btn-lg" onClick={handleAddApp}>
            + Add an app
          </button>
        ) : (
          <div className="tooltip-wrap">
            <button
              type="button"
              className="btn btn-primary btn-lg"
              disabled
              aria-describedby="add-app-hint"
            >
              + Add an app
            </button>
            <span id="add-app-hint" className="sr-only">
              Only an owner or admin can add an app.
            </span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="row between aic">
        <h1 className="h3">Your apps</h1>
        {canAddApp ? (
          <button type="button" className="chip" onClick={handleAddApp}>
            + Add an app
          </button>
        ) : (
          <span className="tooltip-wrap">
            <span
              className="chip"
              aria-disabled="true"
              aria-describedby="add-app-hint"
              style={{ opacity: 0.5 }}
            >
              + Add an app
            </span>
            <span id="add-app-hint" className="sr-only">
              Only an owner or admin can add an app.
            </span>
          </span>
        )}
      </div>
      <div className="grid mt12" style={{ gap: 12 }}>
        {state.apps.map((app) => {
          const status = statusFor(app);
          return (
            <Link
              key={app.id}
              className="card app-card"
              to={`/apps/${app.id}`}
              aria-label={`${app.name}, ${status.label}`}
            >
              <div className="top">
                <span className="app-ico">
                  <span className="win" />
                </span>
                <div className="grow">
                  <div style={{ fontWeight: 600 }}>{app.name}</div>
                </div>
                <span className={`tag ${status.tag}`}>{status.label}</span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};
